import { useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Col, Form, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { FloatInput, FloatSelect, showToast, WizardFooter } from "src/componentes";
import moment from "moment";
import {
  getLocal,
  MDFeInput,
  modalMDFeOptions,
  STORAGEMDFE_KEY,
  tipoEmitenteMDFeOptions,
  tipoTransportadorMDFeOptions,
  ufOptions,
  updateLocal,
} from "../MDFe.Dto";
import {
  IdentificacaoFormType,
  IdentificacaoSchema,
} from "./MDFe.Cadastro.Dto";
import MDFeConfiguracaoCode from "../MDFe.Configuracao.Code";
import { TipoConfiguracaoEnum } from "src/pages/Privada/Configuracoes/Sistema/Sistema.Dto";
import TipoTransportadorAjuda from "./Componentes/TipoTransportadorAjuda";

const MDFeCadastroIdentificacaoView = () => {
  const navigate = useNavigate();
  const configuracao = MDFeConfiguracaoCode();
  const mdfe = getLocal<MDFeInput>(STORAGEMDFE_KEY);

  const form = useForm<IdentificacaoFormType>({
    resolver: yupResolver(IdentificacaoSchema),
    defaultValues: {
      tipoEmitente: mdfe?.tipoEmitente ?? 1,
      tipoTransportador: mdfe?.tipoTransportador ? mdfe.tipoTransportador : undefined,
      modal: mdfe?.modal ?? 1,
      ufInicio: mdfe?.ufInicio || "",
      ufFim: mdfe?.ufFim || "",
      rntrc:
        mdfe?.rntrc ||
        configuracao.getConfiguracaoValor(TipoConfiguracaoEnum.RNTRC) ||
        "",
      previsaoInicioViagemDataHora: mdfe?.previsaoInicioViagemDataHora
        ? moment(mdfe.previsaoInicioViagemDataHora, [
            moment.ISO_8601,
            "YYYY-MM-DDTHH:mm:ss",
            "DD/MM/YYYY HH:mm:ss",
          ]).format("DD/MM/YYYY HH:mm:ss")
        : "",
    },
  });

  // Atribui o RNTRC automaticamente com base na configuração carregada caso ainda não definido
  useEffect(() => {
    if (!configuracao.loading) {
      const rntrcConfig = configuracao.getConfiguracaoValor(
        TipoConfiguracaoEnum.RNTRC
      );
      if (rntrcConfig) {
        const mdfeAtual = getLocal<MDFeInput>(STORAGEMDFE_KEY);
        if (!mdfeAtual?.rntrc && !form.getValues("rntrc")) {
          form.setValue("rntrc", rntrcConfig);
        }
      }
    }
  }, [configuracao.loading]);

  const tipoEmitenteAtual = form.watch("tipoEmitente");

  const handleVoltar = () => {
    navigate("/fiscal/mdfe");
  };

  const onSubmit = (data: IdentificacaoFormType) => {
    let formattedPrevisao: string | undefined = undefined;
    if (data.previsaoInicioViagemDataHora) {
      const parsed = moment(data.previsaoInicioViagemDataHora, [
        "DD/MM/YYYY HH:mm:ss",
        "DD/MM/YYYY HH:mm",
        moment.ISO_8601,
      ]);
      if (parsed.isValid()) {
        formattedPrevisao = parsed.format("YYYY-MM-DDTHH:mm:ss");
      }
    }

    updateLocal<MDFeInput>(STORAGEMDFE_KEY, {
      tipoEmitente: Number(data.tipoEmitente),
      tipoTransportador: data.tipoTransportador ? Number(data.tipoTransportador) : undefined,
      modal: Number(data.modal),
      ufInicio: data.ufInicio,
      ufFim: data.ufFim,
      rntrc: data.rntrc || undefined,
      previsaoInicioViagemDataHora: formattedPrevisao,
      serie: mdfe?.serie || 1,
      numeroMDFe: mdfe?.numeroMDFe ?? 0,
    });

    navigate("../percurso");
  };

  const onError = (errors: any) => {
    if (errors.rntrc) {
      showToast({
        message:
          "O campo RNTRC é obrigatório para Prestadores de Serviço de Transporte e deve conter 8 dígitos.",
        title: "Grupo Obrigatório",
        variant: "Warning",
      });
      return;
    }
    showToast({
      message:
        "Por favor, preencha todos os campos obrigatórios da Identificação (Tipo, Modal e UFs).",
      title: "Grupo Obrigatório",
      variant: "Warning",
    });
  };

  return (
    <Form noValidate onSubmit={form.handleSubmit(onSubmit, onError)} className="mt-3 mb-5">
      <div className="mb-4">
        <h5 className="mb-3 text-primary">Identificação do MDF-e</h5>
        <Row className="g-3">
          <Col xs={12} md={6}>
            <FloatSelect
              label="Tipo de Emitente"
              options={tipoEmitenteMDFeOptions}
              register={form.register("tipoEmitente", { valueAsNumber: true })}
              errorsMessage={form.formState.errors.tipoEmitente}
            />
          </Col>

          <Col xs={12} md={6}>
            <FloatSelect
              label="Tipo de Transportador"
              options={tipoTransportadorMDFeOptions}
              register={form.register("tipoTransportador", {
                valueAsNumber: true,
              })}
              errorsMessage={form.formState.errors.tipoTransportador}
              helpElement={<TipoTransportadorAjuda />}
            />
          </Col>

          <Col xs={12} md={4}>
            <FloatSelect
              label="Modal de Transporte"
              options={modalMDFeOptions}
              register={form.register("modal", { valueAsNumber: true })}
              errorsMessage={form.formState.errors.modal}
            />
          </Col>

          <Col xs={12} md={4}>
            <FloatSelect
              label="UF Início do Carregamento"
              options={ufOptions}
              register={form.register("ufInicio")}
              errorsMessage={form.formState.errors.ufInicio}
            />
          </Col>

          <Col xs={12} md={4}>
            <FloatSelect
              label="UF Fim da Descarga"
              options={ufOptions}
              register={form.register("ufFim")}
              errorsMessage={form.formState.errors.ufFim}
            />
          </Col>

          {Number(tipoEmitenteAtual) === 1 ? (
            <Col xs={12} md={6}>
              <FloatInput
                label="RNTRC (8 dígitos)"
                registerField={form.register("rntrc")}
                errorsMessage={form.formState.errors.rntrc}
                inputMask="int"
                maxLength={8}
              />
            </Col>
          ) : (
            <Col xs={12} md={6}>
              <FloatInput
                label="RNTRC (Opcional)"
                registerField={form.register("rntrc")}
                errorsMessage={form.formState.errors.rntrc}
                inputMask="int"
                maxLength={8}
              />
            </Col>
          )}

          <Col xs={12} md={6}>
            <FloatInput
              label="Previsão de Início da Viagem"
              registerField={form.register("previsaoInicioViagemDataHora")}
              errorsMessage={
                form.formState.errors.previsaoInicioViagemDataHora
              }
              inputMask="datahora"
            />
          </Col>
        </Row>
      </div>

      <WizardFooter onVoltar={handleVoltar} />
    </Form>
  );
};

export default MDFeCadastroIdentificacaoView;
